import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faExchangeAlt, faInbox, faPaperPlane, faChartBar, faPlus,
    faEye, faCheck, faTimes, faHourglass, faFilter
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import { useBaseUrl } from '../../../utils/utils';
import localForage from 'localforage';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

interface ReferralStats {
    total_referrals: number;
    inbound_referrals: number;
    outbound_referrals: number;
    pending_referrals: number;
    accepted_referrals: number;
    rejected_referrals: number;
    completed_referrals: number;
}

interface Referral {
    id: number;
    patient_name: string;
    patient_id: string;
    referring_clinic: string;
    receiving_clinic: string;
    referral_type: string;
    priority: string;
    status: string;
    created_at: string;
    reason: string;
}

const ReferralManagement: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'inbound' | 'outbound' | 'statistics'>('inbound');
    const [stats, setStats] = useState<ReferralStats | null>(null);
    const [inboundReferrals, setInboundReferrals] = useState<Referral[]>([]);
    const [outboundReferrals, setOutboundReferrals] = useState<Referral[]>([]);
    const [loading, setLoading] = useState(true);
    const [clinicId, setClinicId] = useState<number | null>(null);
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        document.title = "Referral Management - Uzima Healthcare";
        initializeData();
    }, []);

    const initializeData = async () => {
        try {
            const id = await localForage.getItem<number>('clinic_id');
            if (id) {
                setClinicId(id);
                await Promise.all([
                    fetchStatistics(id),
                    fetchInboundReferrals(id),
                    fetchOutboundReferrals(id)
                ]);
            }
        } catch (error) {
            console.error('Error initializing:', error);
        }
    };

    const fetchStatistics = async (clinicId: number) => {
        try {
            const response = await fetch(`${baseUrl}facilities/referrals/statistics?clinic_id=${clinicId}`, {
                headers: {
                    'Authorization': `Bearer ${await localForage.getItem('access_token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setStats(data.data || data);
            }
        } catch (error) {
            console.error('Error fetching statistics:', error);
        }
    };

    const fetchInboundReferrals = async (clinicId: number) => {
        setLoading(true);
        try {
            const response = await fetch(`${baseUrl}facilities/referrals/inbound?clinic_id=${clinicId}`, {
                headers: {
                    'Authorization': `Bearer ${await localForage.getItem('access_token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setInboundReferrals(data.data || data);
            }
        } catch (error) {
            console.error('Error fetching inbound referrals:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchOutboundReferrals = async (clinicId: number) => {
        setLoading(true);
        try {
            const response = await fetch(`${baseUrl}facilities/referrals/outbound?clinic_id=${clinicId}`, {
                headers: {
                    'Authorization': `Bearer ${await localForage.getItem('access_token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setOutboundReferrals(data.data || data);
            }
        } catch (error) {
            console.error('Error fetching outbound referrals:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRespondToReferral = async (referralId: number, action: 'accept' | 'reject') => {
        const result = await Swal.fire({
            title: `${action === 'accept' ? 'Accept' : 'Reject'} Referral?`,
            text: action === 'accept' ? 'You will be responsible for this patient' : 'This referral will be rejected',
            icon: 'question',
            input: 'textarea',
            inputLabel: 'Response Notes',
            inputPlaceholder: 'Enter your response notes...',
            showCancelButton: true,
            confirmButtonText: action === 'accept' ? 'Accept' : 'Reject',
            confirmButtonColor: action === 'accept' ? '#28a745' : '#dc3545'
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${baseUrl}facilities/referrals/respond`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${await localForage.getItem('access_token')}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        referral_id: referralId,
                        action: action,
                        response_notes: result.value || '',
                        clinic_id: clinicId
                    })
                });

                if (response.ok) {
                    await Swal.fire('Success', `Referral ${action}ed successfully`, 'success');
                    if (clinicId) {
                        await fetchInboundReferrals(clinicId);
                        await fetchStatistics(clinicId);
                    }
                } else {
                    throw new Error('Failed to respond to referral');
                }
            } catch (error) {
                console.error('Error responding to referral:', error);
                Swal.fire('Error', 'Failed to respond to referral', 'error');
            }
        }
    };

    const viewReferralDetails = async (referralId: number) => {
        try {
            const response = await fetch(`${baseUrl}facilities/referrals/details`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await localForage.getItem('access_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ referral_id: referralId })
            });
            const data = await response.json();
            if (response.ok && data.status === 'success') {
                const r = data.data;
                Swal.fire({
                    title: `Referral #${r.referral_id || referralId}`,
                    html: `
                        <div style="text-align:left">
                            <p><strong>Patient:</strong> ${r.patient_name || ''}</p>
                            <p><strong>Type:</strong> ${r.referral_type || ''} &nbsp; <strong>Priority:</strong> ${r.priority || ''}</p>
                            <p><strong>Status:</strong> ${r.status || ''}</p>
                            <p><strong>Reason:</strong> ${r.reason_for_referral || '—'}</p>
                            <p><strong>Clinical Summary:</strong> ${r.clinical_summary || '—'}</p>
                            <p><strong>Requested Services:</strong> ${r.requested_services || '—'}</p>
                        </div>
                    `,
                    width: 600,
                });
            } else {
                Swal.fire('Error', data.message || 'Could not load referral details.', 'error');
            }
        } catch {
            Swal.fire('Error', 'Network error while loading referral details.', 'error');
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status.toLowerCase()) {
            case 'pending': return 'bg-warning';
            case 'accepted': return 'bg-success';
            case 'rejected': return 'bg-danger';
            case 'completed': return 'bg-info';
            default: return 'bg-secondary';
        }
    };

    const getPriorityBadge = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'urgent': return 'bg-danger';
            case 'high': return 'bg-warning';
            case 'normal': return 'bg-info';
            case 'low': return 'bg-secondary';
            default: return 'bg-secondary';
        }
    };

    const filteredInbound = filterStatus === 'all' 
        ? inboundReferrals 
        : inboundReferrals.filter(r => r.status.toLowerCase() === filterStatus);

    const filteredOutbound = filterStatus === 'all' 
        ? outboundReferrals 
        : outboundReferrals.filter(r => r.status.toLowerCase() === filterStatus);

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h2 className="mb-1">
                                            <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                                            Referral Management
                                        </h2>
                                        <p className="text-muted">Manage patient referrals</p>
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => navigate('/institution/clinician/referrals')}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                        Create Referral
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Statistics Cards */}
                        {stats && (
                            <div className="row mb-4">
                                <div className="col-md-3">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0">
                                                    <div className="avatar-sm rounded-circle bg-primary">
                                                        <span className="avatar-title">
                                                            <FontAwesomeIcon icon={faInbox} className="text-white" />
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1 ms-3">
                                                    <h5 className="mb-0">{stats.inbound_referrals}</h5>
                                                    <p className="text-muted mb-0">Inbound</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0">
                                                    <div className="avatar-sm rounded-circle bg-success">
                                                        <span className="avatar-title">
                                                            <FontAwesomeIcon icon={faPaperPlane} className="text-white" />
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1 ms-3">
                                                    <h5 className="mb-0">{stats.outbound_referrals}</h5>
                                                    <p className="text-muted mb-0">Outbound</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0">
                                                    <div className="avatar-sm rounded-circle bg-warning">
                                                        <span className="avatar-title">
                                                            <FontAwesomeIcon icon={faHourglass} className="text-white" />
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1 ms-3">
                                                    <h5 className="mb-0">{stats.pending_referrals}</h5>
                                                    <p className="text-muted mb-0">Pending</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0">
                                                    <div className="avatar-sm rounded-circle bg-info">
                                                        <span className="avatar-title">
                                                            <FontAwesomeIcon icon={faChartBar} className="text-white" />
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1 ms-3">
                                                    <h5 className="mb-0">{stats.total_referrals}</h5>
                                                    <p className="text-muted mb-0">Total</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Tabs */}
                        <ul className="nav nav-tabs nav-tabs-custom mb-3">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'inbound' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('inbound')}
                                >
                                    <FontAwesomeIcon icon={faInbox} className="me-2" />
                                    Inbound Referrals
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'outbound' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('outbound')}
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                                    Outbound Referrals
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'statistics' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('statistics')}
                                >
                                    <FontAwesomeIcon icon={faChartBar} className="me-2" />
                                    Statistics
                                </button>
                            </li>
                        </ul>

                        {/* Filter */}
                        <div className="card mb-3">
                            <div className="card-body">
                                <div className="row align-items-center">
                                    <div className="col-md-4">
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <FontAwesomeIcon icon={faFilter} />
                                            </span>
                                            <select
                                                className="form-select"
                                                value={filterStatus}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                            >
                                                <option value="all">All Status</option>
                                                <option value="pending">Pending</option>
                                                <option value="accepted">Accepted</option>
                                                <option value="rejected">Rejected</option>
                                                <option value="completed">Completed</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Inbound Referrals */}
                                {activeTab === 'inbound' && (
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Patient</th>
                                                            <th>From</th>
                                                            <th>Type</th>
                                                            <th>Priority</th>
                                                            <th>Status</th>
                                                            <th>Date</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredInbound.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={7} className="text-center py-4">
                                                                    No inbound referrals found
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            filteredInbound.map((referral) => (
                                                                <tr key={referral.id}>
                                                                    <td>
                                                                        <strong>{referral.patient_name}</strong>
                                                                        <br />
                                                                        <small className="text-muted">{referral.patient_id}</small>
                                                                    </td>
                                                                    <td>{referral.referring_clinic}</td>
                                                                    <td>{referral.referral_type}</td>
                                                                    <td>
                                                                        <span className={`badge ${getPriorityBadge(referral.priority)}`}>
                                                                            {referral.priority}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span className={`badge ${getStatusBadge(referral.status)}`}>
                                                                            {referral.status}
                                                                        </span>
                                                                    </td>
                                                                    <td>{new Date(referral.created_at).toLocaleDateString()}</td>
                                                                    <td>
                                                                        <div className="btn-group btn-group-sm">
                                                                            <button 
                                                                                className="btn btn-outline-primary"
                                                                                onClick={() => viewReferralDetails(referral.id)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faEye} />
                                                                            </button>
                                                                            {referral.status.toLowerCase() === 'pending' && (
                                                                                <>
                                                                                    <button 
                                                                                        className="btn btn-outline-success"
                                                                                        onClick={() => handleRespondToReferral(referral.id, 'accept')}
                                                                                    >
                                                                                        <FontAwesomeIcon icon={faCheck} />
                                                                                    </button>
                                                                                    <button 
                                                                                        className="btn btn-outline-danger"
                                                                                        onClick={() => handleRespondToReferral(referral.id, 'reject')}
                                                                                    >
                                                                                        <FontAwesomeIcon icon={faTimes} />
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Outbound Referrals */}
                                {activeTab === 'outbound' && (
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="table-responsive">
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Patient</th>
                                                            <th>To</th>
                                                            <th>Type</th>
                                                            <th>Priority</th>
                                                            <th>Status</th>
                                                            <th>Date</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {filteredOutbound.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={7} className="text-center py-4">
                                                                    No outbound referrals found
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            filteredOutbound.map((referral) => (
                                                                <tr key={referral.id}>
                                                                    <td>
                                                                        <strong>{referral.patient_name}</strong>
                                                                        <br />
                                                                        <small className="text-muted">{referral.patient_id}</small>
                                                                    </td>
                                                                    <td>{referral.receiving_clinic}</td>
                                                                    <td>{referral.referral_type}</td>
                                                                    <td>
                                                                        <span className={`badge ${getPriorityBadge(referral.priority)}`}>
                                                                            {referral.priority}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span className={`badge ${getStatusBadge(referral.status)}`}>
                                                                            {referral.status}
                                                                        </span>
                                                                    </td>
                                                                    <td>{new Date(referral.created_at).toLocaleDateString()}</td>
                                                                    <td>
                                                                        <button 
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={() => viewReferralDetails(referral.id)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faEye} className="me-1" />
                                                                            View
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Statistics Tab */}
                                {activeTab === 'statistics' && stats && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h5 className="mb-0">Referral Status Breakdown</h5>
                                                </div>
                                                <div className="card-body">
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span>Pending</span>
                                                            <strong>{stats.pending_referrals}</strong>
                                                        </div>
                                                        <div className="progress mb-3">
                                                            <div
                                                                className="progress-bar bg-warning"
                                                                style={{width: `${stats.total_referrals > 0 ? (stats.pending_referrals / stats.total_referrals) * 100 : 0}%`}}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span>Accepted</span>
                                                            <strong>{stats.accepted_referrals}</strong>
                                                        </div>
                                                        <div className="progress mb-3">
                                                            <div
                                                                className="progress-bar bg-success"
                                                                style={{width: `${stats.total_referrals > 0 ? (stats.accepted_referrals / stats.total_referrals) * 100 : 0}%`}}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span>Rejected</span>
                                                            <strong>{stats.rejected_referrals}</strong>
                                                        </div>
                                                        <div className="progress mb-3">
                                                            <div
                                                                className="progress-bar bg-danger"
                                                                style={{width: `${stats.total_referrals > 0 ? (stats.rejected_referrals / stats.total_referrals) * 100 : 0}%`}}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span>Completed</span>
                                                            <strong>{stats.completed_referrals}</strong>
                                                        </div>
                                                        <div className="progress">
                                                            <div
                                                                className="progress-bar bg-info"
                                                                style={{width: `${stats.total_referrals > 0 ? (stats.completed_referrals / stats.total_referrals) * 100 : 0}%`}}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="card">
                                                <div className="card-header">
                                                    <h5 className="mb-0">Quick Stats</h5>
                                                </div>
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between py-3 border-bottom">
                                                        <span>Total Referrals</span>
                                                        <strong>{stats.total_referrals}</strong>
                                                    </div>
                                                    <div className="d-flex justify-content-between py-3 border-bottom">
                                                        <span>Acceptance Rate</span>
                                                        <strong>
                                                            {stats.total_referrals > 0 
                                                                ? ((stats.accepted_referrals / stats.total_referrals) * 100).toFixed(1) 
                                                                : 0}%
                                                        </strong>
                                                    </div>
                                                    <div className="d-flex justify-content-between py-3 border-bottom">
                                                        <span>Completion Rate</span>
                                                        <strong>
                                                            {stats.total_referrals > 0 
                                                                ? ((stats.completed_referrals / stats.total_referrals) * 100).toFixed(1) 
                                                                : 0}%
                                                        </strong>
                                                    </div>
                                                    <div className="d-flex justify-content-between py-3">
                                                        <span>Inbound vs Outbound</span>
                                                        <strong>{stats.inbound_referrals} / {stats.outbound_referrals}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DashboardFooter />
            </div>
        </>
    );
};

export default ReferralManagement;

